import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, CheckCircle2, Clock, XCircle, FileText, Send, MessageSquare, LifeBuoy } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ChatThread from "../components/ChatThread";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { SERVICES } from "../mock";

function toBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function CaregiverDashboard() {
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState(user?.status || "incomplete");
  const [bio, setBio] = useState(user?.bio || "");
  const [services, setServices] = useState(user?.services || []);
  const [idDoc, setIdDoc] = useState(null);
  const [resume, setResume] = useState(null);
  const [saving, setSaving] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [myApps, setMyApps] = useState([]);
  const [chatApp, setChatApp] = useState(null);
  const [supportOpen, setSupportOpen] = useState(false);

  useEffect(() => { setStatus(user?.status || "incomplete"); setBio(user?.bio || ""); setServices(user?.services || []); }, [user]);

  useEffect(() => {
    if (status === "approved") {
      api.get("/jobs/public").then(({ data }) => setJobs(data)).catch(() => {});
      api.get("/applications/mine").then(({ data }) => setMyApps(data)).catch(() => {});
    }
  }, [status]);

  const toggleService = (id) => setServices((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const save = async (e) => {
    e.preventDefault();
    if (!idDoc || !resume) return toast.error("Please upload your ID and resume.");
    setSaving(true);
    try {
      const [idB64, resumeB64] = await Promise.all([toBase64(idDoc), toBase64(resume)]);
      const { data } = await api.put("/caregiver/documents", { id_doc: idB64, resume: resumeB64, bio, services });
      setStatus(data.status);
      refresh && refresh();
      toast.success("Submitted! We'll review your profile shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally { setSaving(false); }
  };

  const appliedIds = new Set(myApps.map((a) => a.job_id));
  const applyJob = async (job) => {
    try {
      await api.post("/applications", { job_id: job.id, message: `Hi, I'd love to help with your ${job.service} role.` });
      toast.success("Application sent!");
      const { data } = await api.get("/applications/mine"); setMyApps(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not apply."); }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put("/caregiver/profile", { bio, services });
      refresh && refresh();
      toast.success("Profile updated — families see this when you apply.");
    } catch (e) { toast.error("Could not update profile."); }
    finally { setSaving(false); }
  };

  const banner = {
    pending: { icon: Clock, cls: "bg-blue-50 text-blue-700 border-blue-200", text: "Your profile is under review. We'll email you once approved." },
    approved: { icon: CheckCircle2, cls: "bg-green-50 text-green-700 border-green-200", text: "You're approved! Browse and apply to jobs below." },
    rejected: { icon: XCircle, cls: "bg-red-50 text-red-700 border-red-200", text: "Your profile wasn't approved. Please contact support." },
  }[status];

  return (
    <div>
      <Header />
      <div className="bg-[#fdfaf7] min-h-[70vh]">
        <div className="max-w-4xl mx-auto px-5 py-12">
          <h1 className="font-display text-3xl text-[#1f1a1b]">Caregiver dashboard</h1>
          {banner && (
            <div className={`mt-4 flex items-center gap-2 rounded-xl border px-4 py-3 text-[14px] ${banner.cls}`}>
              <banner.icon className="w-5 h-5" /> {banner.text}
            </div>
          )}

          <div className="mt-4 rounded-2xl border border-black/10 bg-white overflow-hidden">
            <button onClick={() => setSupportOpen(!supportOpen)} data-testid="caregiver-support-toggle" className="w-full flex items-center justify-between px-5 py-4">
              <span className="flex items-center gap-2 font-display text-lg text-[#1f1a1b]"><LifeBuoy className="w-5 h-5 text-[#7c2233]" /> Messages from SimplySitter</span>
              <span className="text-[13px] text-[#7c2233]">{supportOpen ? "Hide" : "Open"}</span>
            </button>
            {supportOpen && (
              <div className="px-5 pb-5">
                <p className="text-[13px] text-[#8a8285] mb-2">The SimplySitter team may message you here (e.g. to remind you to upload your ID & CV). You can reply too.</p>
                <ChatThread threadType="support" threadId={user?.id} meId={user?.id} placeholder="Message the SimplySitter team…" />
              </div>
            )}
          </div>

          {status !== "approved" && (
            <form onSubmit={save} className="mt-6 rounded-2xl border border-black/10 bg-white p-7 space-y-5">
              <div>
                <h2 className="font-display text-xl text-[#1f1a1b]">Complete your profile</h2>
                <p className="text-[14px] text-[#5a5254]">Upload your ID and resume. We verify every caregiver before they can apply.</p>
              </div>
              <div>
                <label className="text-[13px] font-medium text-[#3a3436]">Services you offer</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                  {SERVICES.map((s) => (
                    <button type="button" key={s.id} onClick={() => toggleService(s.id)}
                      className={`rounded-xl border px-2 py-2.5 text-[13px] font-medium ${services.includes(s.id) ? "border-[#7c2233] bg-[#f7e4e6] text-[#7c2233]" : "border-black/10 text-[#5a5254]"}`}>{s.label}</button>
                  ))}
                </div>
              </div>
              <FileInput label="Photo ID (passport / driver's license)" file={idDoc} onChange={setIdDoc} accept="image/*,application/pdf" />
              <FileInput label="Resume / CV" file={resume} onChange={setResume} accept="application/pdf,image/*,.doc,.docx" />
              <div>
                <label className="text-[13px] font-medium text-[#3a3436]">Short bio</label>
                <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={3} placeholder="Experience, availability, languages…"
                  className="mt-1.5 w-full rounded-xl border border-black/10 px-3 py-2.5 text-[15px] outline-none focus:border-[#7c2233] focus:ring-2 focus:ring-[#7c2233]/15 resize-none" />
              </div>
              <button type="submit" disabled={saving}
                className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-xl bg-[#7c2233] hover:bg-[#661b29] disabled:opacity-70 text-white font-medium transition-colors">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Submit for review
              </button>
            </form>
          )}

          {status === "approved" && (
            <div className="mt-8">
              <div className="mb-8 rounded-2xl border border-black/10 bg-white p-6" data-testid="caregiver-profile-card">
                <h2 className="font-display text-xl text-[#1f1a1b]">Your profile</h2>
                <p className="text-[13px] text-[#8a8285] mb-3">Keep your bio and services current — families see this when you apply.</p>
                <label className="text-[13px] font-medium text-[#3a3436]">Services you offer</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 mb-4">
                  {SERVICES.map((s) => (
                    <button type="button" key={s.id} onClick={() => toggleService(s.id)}
                      className={`rounded-xl border px-2 py-2.5 text-[13px] font-medium ${services.includes(s.id) ? "border-[#7c2233] bg-[#f7e4e6] text-[#7c2233]" : "border-black/10 text-[#5a5254]"}`}>{s.label}</button>
                  ))}
                </div>
                <label className="text-[13px] font-medium text-[#3a3436]">Short bio</label>
                <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={3} data-testid="caregiver-bio-input" placeholder="Experience, availability, languages…"
                  className="mt-1.5 w-full rounded-xl border border-black/10 px-3 py-2.5 text-[15px] outline-none focus:border-[#7c2233] focus:ring-2 focus:ring-[#7c2233]/15 resize-none" />
                <button onClick={saveProfile} disabled={saving} data-testid="caregiver-save-profile"
                  className="mt-3 inline-flex items-center gap-2 rounded-xl bg-[#7c2233] hover:bg-[#661b29] disabled:opacity-70 text-white font-medium px-5 py-2.5 transition-colors">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Save profile
                </button>
              </div>

              <h2 className="font-display text-2xl text-[#1f1a1b]">Open jobs</h2>
              {jobs.length === 0 ? (
                <p className="text-[14px] text-[#8a8285] mt-3">No open jobs right now. Check back soon.</p>
              ) : (
                <div className="mt-4 space-y-3">
                  {jobs.map((j) => (
                    <div key={j.id} className="rounded-2xl border border-black/10 bg-white p-5 flex items-center justify-between gap-4">
                      <div>
                        <h3 className="font-display text-lg text-[#1f1a1b] capitalize">{j.service} — {j.location}</h3>
                        {j.details && <p className="text-[14px] text-[#5a5254] mt-1">{j.details}</p>}
                      </div>
                      {appliedIds.has(j.id) ? (
                        <span className="text-[13px] font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-1">Applied</span>
                      ) : (
                        <button onClick={() => applyJob(j)} className="inline-flex items-center gap-1.5 text-[14px] font-medium text-white bg-[#7c2233] hover:bg-[#661b29] rounded-lg px-4 py-2 transition-colors">
                          <Send className="w-4 h-4" /> Apply
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {myApps.length > 0 && (
                <div className="mt-10">
                  <h2 className="font-display text-2xl text-[#1f1a1b]">My applications</h2>
                  <div className="mt-4 space-y-3">
                    {myApps.map((a) => (
                      <div key={a.id} className="rounded-2xl border border-black/10 bg-white p-5">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div>
                            <h3 className="font-display text-lg text-[#1f1a1b] capitalize">{a.job_service || "Job"} — {a.job_location || ""}</h3>
                            {a.has_messages ? (
                              <span className="inline-flex items-center gap-1 text-[12px] font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-0.5 mt-1">Family contacted you</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[12px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5 mt-1">Submitted — awaiting family</span>
                            )}
                          </div>
                          {a.has_messages ? (
                            <button data-testid={`caregiver-view-messages-${a.id}`} onClick={() => setChatApp(chatApp === a.id ? null : a.id)} className="inline-flex items-center gap-1.5 text-[14px] font-medium text-white bg-[#7c2233] hover:bg-[#661b29] rounded-lg px-4 py-2 transition-colors">
                              <MessageSquare className="w-4 h-4" /> {chatApp === a.id ? "Hide messages" : "New message from family"}
                            </button>
                          ) : (
                            <span data-testid={`caregiver-awaiting-family-${a.id}`} className="text-[13px] text-[#8a8285] max-w-[240px] text-right">If a family is interested, they'll message you here.</span>
                          )}
                        </div>
                        {a.has_messages && chatApp === a.id && (
                          <div className="mt-3">
                            <ChatThread threadType="application" threadId={a.id} meId={user?.id} placeholder="Reply to the family…" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

function FileInput({ label, file, onChange, accept }) {
  return (
    <div>
      <label className="text-[13px] font-medium text-[#3a3436]">{label}</label>
      <label className="mt-1.5 flex items-center gap-2 rounded-xl border border-dashed border-black/20 px-4 py-3 cursor-pointer hover:border-[#7c2233] transition-colors">
        <FileText className="w-4 h-4 text-[#7c2233]" />
        <span className="text-[14px] text-[#5a5254] truncate">{file ? file.name : "Choose a file…"}</span>
        <input type="file" accept={accept} className="hidden" onChange={(e) => onChange(e.target.files?.[0] || null)} />
      </label>
    </div>
  );
}
